import React, { useEffect, useRef } from "react";
import "./MergeGame.css";
import { MergeResult } from "./mergeGame.ts";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface MergeGameSummaryProps {
    result: MergeResult;
    /** Best score at this level on this device, after this run. */
    best: number | null;
    isNewBest: boolean;
    /** Where the practice behind this level lives, by slug; looked up in the manifest. */
    practiceSlugs: string[];
    onPlayAgain(): void;
}

/**
 * The end of a run (ADR 0128 §4): targets made, merges refused, the best at this
 * level, and the worksheets and quiz that practise what the level was about.
 * Takes the focus when it appears, so a keyboard or screen reader user lands on
 * it rather than on a board that has just stopped.
 */
export function MergeGameSummary({ result, best, isNewBest, practiceSlugs, onPlayAgain }: MergeGameSummaryProps) {
    const t = useTranslation();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        ref.current?.focus({ preventScroll: true });
    }, []);

    const links = practiceSlugs.flatMap((slug) => {
        const route = findBySlug(slug);
        return route ? [{ slug: route.slug, text: route.h1 ?? route.slug }] : [];
    });

    return (
        <div className="mergeSummary" ref={ref} tabIndex={-1}>
            <p className="mergeSummaryHeading">{t("flashCards.mergeFinal")}</p>
            <p className="mergeSummaryScore">{result.targetsMade}</p>
            <p className="mergeSummaryCaption">{t("flashCards.mergeScoreCaption")}</p>
            <p className="mergeSummaryCounts">
                {format(t("flashCards.mergeSummaryRefusedTemplate"), { n: result.refusedMerges })}
            </p>

            {best !== null && (
                <p className={`mergeSummaryBest${isNewBest ? " mergeSummaryBest--new" : ""}`}>
                    {isNewBest ? t("flashCards.newBest") : format(t("flashCards.yourBestTemplate"), { n: best })}
                </p>
            )}

            <button type="button" className="btn btn-primary mergePlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>

            {links.length > 0 && (
                <div className="mergePractice">
                    <h3>{t("flashCards.mergePracticeHeading")}</h3>
                    <ul>
                        {links.map((link) => (
                            <li key={link.slug}>
                                <a href={link.slug}>{link.text}</a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default MergeGameSummary;
